import React from 'react';
import dayjs from 'dayjs';
import { Box, Button, ButtonBase, IconButton, Typography } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { SearchField } from '../inputs/SearchField';

export interface ConversationItem {
  id: string;
  title: string | null;
  /** `agent` — a saved agent's run, marked so it isn't mistaken for a chat. */
  kind?: 'chat' | 'agent';
  /** ISO date the conversation started, for grouping. */
  date?: string | null;
}

export interface ConversationListProps {
  items: ConversationItem[];
  /** The conversation open now; highlighted. */
  activeId?: string | null;
  onSelect: (item: ConversationItem) => void;
  /** Adds a New chat button on top. Leave out where one sits nearby, e.g. in a header. */
  onNewChat?: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Put the cursor in the search box on opening — in a pop-over. */
  autoFocusSearch?: boolean;
  /**
   * Adds a delete button to each row — shown on hover or keyboard focus, and
   * always on touch screens. It only asks: confirm before deleting anything.
   */
  onDelete?: (item: ConversationItem) => void;
  /** The first page is loading. */
  loading?: boolean;
  /** More conversations exist than are shown. */
  hasMore?: boolean;
  onShowMore?: () => void;
  loadingMore?: boolean;
}

const GROUPS = ['Today', 'Yesterday', 'Last 7 days', 'Older'] as const;

function groupOf(date: string | null | undefined, today: dayjs.Dayjs): (typeof GROUPS)[number] {
  if (!date) return 'Older';
  const d = dayjs(date);
  if (!d.isValid()) return 'Older';
  if (d.isSame(today, 'day')) return 'Today';
  if (d.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isAfter(today.subtract(7, 'day'))) return 'Last 7 days';
  return 'Older';
}

/**
 * A person's conversations, for a sidebar or a pop-over: search, pick up an
 * earlier one, and start a new one. Grouped by when they started, newest
 * first as given; the open one is highlighted. Fills the height it's given
 * and scrolls below the search box.
 */
export function ConversationList({
  items,
  activeId,
  onSelect,
  onNewChat,
  search,
  onSearchChange,
  autoFocusSearch = false,
  onDelete,
  loading,
  hasMore,
  onShowMore,
  loadingMore,
}: ConversationListProps) {
  const today = dayjs();
  const grouped = GROUPS.map((label) => ({
    label,
    items: items.filter((item) => groupOf(item.date, today) === label),
  })).filter((g) => g.items.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box sx={{ px: 1, pt: 1.25, pb: 1, display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
        {onNewChat && (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<AddRounded fontSize="small" />}
          onClick={onNewChat}
          sx={(t) => ({
            justifyContent: 'flex-start',
            minHeight: t.ds.sizes.control.md,
            borderColor: t.tokens.border.default,
            bgcolor: t.tokens.surface.raised,
            color: t.tokens.text.primary,
            '&:hover': { borderColor: t.tokens.border.strong, bgcolor: t.tokens.surface.raised },
          })}
        >
          New chat
        </Button>
        )}
        <SearchField
          value={search}
          onChange={onSearchChange}
          placeholder="Search conversations…"
          autoFocus={autoFocusSearch}
        />
      </Box>

      <Box component="nav" aria-label="Conversations" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1 }}>
        {loading && items.length === 0 && (
          <Typography variant="caption" component="p" sx={(t) => ({ px: 1, py: 2, color: t.tokens.text.tertiary })}>
            Loading conversations…
          </Typography>
        )}
        {!loading && items.length === 0 && (
          <Typography variant="caption" component="p" sx={(t) => ({ px: 1, py: 2, color: t.tokens.text.tertiary })}>
            {search.trim() ? 'No conversations match that.' : 'No conversations yet.'}
          </Typography>
        )}
        {grouped.map((group) => (
          <Box key={group.label} sx={{ mb: 1 }}>
            <Typography
              component="h3"
              sx={(t) => ({ ...t.typography.eyebrow, m: 0, px: 1, pt: 1, pb: 0.5, color: t.tokens.text.tertiary })}
            >
              {group.label}
            </Typography>
            {group.items.map((item) => {
              const active = item.id === activeId;
              const Icon = item.kind === 'agent' ? SmartToyOutlined : ChatBubbleOutlineRounded;
              const name = item.title || 'Untitled chat';
              return (
                <Box
                  key={item.id}
                  sx={{
                    position: 'relative',
                    '&:hover .conversation-delete, &:focus-within .conversation-delete': { opacity: 1 },
                  }}
                >
                <ButtonBase
                  onClick={() => onSelect(item)}
                  aria-current={active ? 'page' : undefined}
                  title={item.title || 'Untitled chat'}
                  sx={(t) => ({
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    pl: 1,
                    // Room for the delete button, so it never covers the name.
                    pr: onDelete ? 5 : 1,
                    py: 0.75,
                    textAlign: 'left',
                    borderRadius: `${t.ds.radius.sm}px`,
                    color: active ? t.tokens.sidebar.itemActiveFg : t.tokens.sidebar.itemFg,
                    bgcolor: active ? t.tokens.sidebar.itemActiveBg : 'transparent',
                    '&:hover': { bgcolor: active ? t.tokens.sidebar.itemActiveBg : t.tokens.sidebar.itemHoverBg },
                    '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
                  })}
                >
                  <Icon fontSize="small" sx={{ opacity: active ? 1 : 0.6, flexShrink: 0 }} aria-hidden />
                  <Typography variant="body2" noWrap sx={{ color: 'inherit', fontWeight: active ? 600 : 400, minWidth: 0 }}>
                    {item.title || 'Untitled chat'}
                  </Typography>
                  {item.kind === 'agent' && (
                    <Box component="span" sx={visuallyHiddenSx}>(agent run)</Box>
                  )}
                </ButtonBase>
                {onDelete && (
                  <IconButton
                    className="conversation-delete"
                    size="small"
                    aria-label={`Delete ${name}`}
                    onClick={() => onDelete(item)}
                    sx={(t) => ({
                      position: 'absolute',
                      right: t.spacing(0.5),
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: 0,
                      color: t.tokens.text.tertiary,
                      transition: `opacity ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                      '&:hover': { color: t.tokens.semantic.danger, bgcolor: t.tokens.semantic.dangerSoft },
                      '&.Mui-focusVisible': { opacity: 1 },
                      '@media (hover: none)': { opacity: 1 },
                    })}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                )}
                </Box>
              );
            })}
          </Box>
        ))}
        {hasMore && onShowMore && (
          <Button size="small" color="inherit" disabled={loadingMore} onClick={onShowMore} sx={{ ml: 0.5 }}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        )}
      </Box>
    </Box>
  );
}

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
